import { useMcpToolsMetadataQuery } from "@qw/graphql";
import { MCPPlatform, type MCPToolName } from "@qw/qw-common";
import { useCurrentEditor, useEditorState } from "@tiptap/react";
import { type FC, type RefObject, useCallback, useMemo, useState } from "react";

import { useElementFromRef } from "../../hooks";
import { cn } from "../../utils";
import { Button } from "../button";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList, CommandLoading } from "../command";
import { Icon } from "../icon";
import { Loading } from "../loading";
import { Popover, PopoverContent, PopoverTrigger } from "../popover";
import { Separator } from "../separator";
import { EditorButton } from "./editor-buttons";

type ToolFilterMode = "all" | "whitelist" | "blacklist";

interface EditorDropdownMenuChatToolNameProps {
  containerRef?: RefObject<HTMLDivElement | null>;

  // Tool filter integration (all optional - feature disabled when omitted)
  toolFilterMode?: ToolFilterMode | null;
  filteredTools?: string[]; // current whitelist or blacklist contents
  onEnableWhitelist?: () => void;
  onAddToWhitelist?: (toolName: string) => void;
  onRemoveFromBlacklist?: (toolName: string) => void;
}

export const EditorDropdownMenuChatToolName: FC<EditorDropdownMenuChatToolNameProps> = ({
  containerRef,
  toolFilterMode,
  filteredTools = [],
  onEnableWhitelist,
  onAddToWhitelist,
  onRemoveFromBlacklist,
}) => {
  const container = useElementFromRef(containerRef);
  const [isOpen, setIsOpen] = useState(false);
  const { editor } = useCurrentEditor();

  const { data, loading } = useMcpToolsMetadataQuery({
    variables: { input: { platforms: [MCPPlatform.INBOX] } },
  });

  const tools = useMemo(
    () => [...(data?.mcpToolsMetadata ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [data?.mcpToolsMetadata]
  );

  const editorState = useEditorState({
    editor,
    selector: (ctx) => {
      const selectedOption = tools.find((tool) => ctx.editor?.isActive("chatToolName", { toolName: tool.name }));
      const defaultValue = selectedOption?.name ?? undefined;

      return { selectedOption, defaultValue };
    },
  });

  const handleSelect = useCallback(
    (value: MCPToolName) => editor?.chain().focus().insertChatToolName(value).run(),
    [editor]
  );

  const handleCloseAutoFocus = useCallback((event: Event) => {
    event.preventDefault();
  }, []);

  if (!editor) {
    return null;
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <EditorButton icon="build" className="max-w-40 pr-2">
          <span className="truncate">{editorState?.defaultValue ?? "AI Tools"}</span>
          <Icon icon="keyboard_arrow_down" className="size-4" />
        </EditorButton>
      </PopoverTrigger>

      {/* NOTE: We need to conditionally render the dropdown menu content to ensure the dropdown menu is properly
      positioned with respect to the container ref when the component is mounted or refreshed. */}
      {isOpen && (
        <PopoverContent
          align="start"
          onCloseAutoFocus={handleCloseAutoFocus}
          container={container}
          className="w-auto p-0"
        >
          <Command defaultValue={editorState?.defaultValue}>
            <div className="p-1">
              <CommandInput placeholder="Search..." />
            </div>

            {loading && !tools?.length && (
              <>
                <Separator />
                <CommandLoading>
                  <Loading label={<>Loading options&hellip;</>} />
                </CommandLoading>
              </>
            )}

            {!!tools?.length && (
              <>
                <Separator />
                <CommandList defaultValue={editorState?.defaultValue} className="max-h-88 max-w-88 p-1">
                  {!loading && <CommandEmpty>No options found.</CommandEmpty>}

                  {tools.map((tool) => {
                    const isSelected = tool.name === editorState?.selectedOption?.name;
                    const description = tool.shortDescription || tool.description;

                    // Tool filter status
                    const isInFilterList = filteredTools.includes(tool.name);
                    const isWhitelistMode = toolFilterMode === "whitelist";
                    const isBlacklistMode = toolFilterMode === "blacklist";
                    const showAddToWhitelist = isWhitelistMode && !isInFilterList && onAddToWhitelist;
                    const showRemoveFromBlacklist = isBlacklistMode && isInFilterList && onRemoveFromBlacklist;

                    return (
                      <CommandItem
                        key={tool.name}
                        value={`${tool.name} | ${description}`}
                        onSelect={() => handleSelect(tool.name as MCPToolName)}
                      >
                        <Icon icon="check" className={cn("flex-none size-4 text-base", { "opacity-0": !isSelected })} />
                        <div className="flex flex-auto flex-col min-w-0 max-w-full gap-0.5 py-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm flex-1">{description}</span>
                            {/* Whitelist mode: show add action for tools not in whitelist */}
                            {showAddToWhitelist && (
                              <Button
                                type="button"
                                variant="link"
                                size="sm"
                                className="h-auto p-0 text-[9px] shrink-0"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSelect(tool.name as MCPToolName);
                                  onAddToWhitelist(tool.name);
                                  setIsOpen(false);
                                }}
                              >
                                + Add & Insert
                              </Button>
                            )}
                            {/* Blacklist mode: show prohibited indicator with unblock action */}
                            {isBlacklistMode && isInFilterList && (
                              <span className="text-[10px] text-destructive shrink-0">prohibited</span>
                            )}
                            {showRemoveFromBlacklist && (
                              <Button
                                type="button"
                                variant="link"
                                size="sm"
                                className="h-auto p-0 text-[10px] text-destructive shrink-0 ml-1"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSelect(tool.name as MCPToolName);
                                  onRemoveFromBlacklist(tool.name);
                                  setIsOpen(false);
                                }}
                              >
                                Unblock & Insert
                              </Button>
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground">{tool.name}</span>
                        </div>
                      </CommandItem>
                    );
                  })}
                </CommandList>
              </>
            )}

            {/* Tool filter banner for "all tools" mode */}
            {(toolFilterMode === "all" || toolFilterMode === null) && onEnableWhitelist && (
              <>
                <Separator />
                <div className="px-3 py-2 text-xs text-muted-foreground bg-muted/50">
                  <div className="flex items-start gap-2">
                    <Icon icon="warning" className="size-4 text-amber-500 shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <p className="text-[10px]">
                        All {tools.length} tools available. This can reduce focus and quality.
                      </p>
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-[10px]"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEnableWhitelist();
                        }}
                      >
                        Enable whitelist mode
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </Command>
        </PopoverContent>
      )}
    </Popover>
  );
};
